import '../styles/index.css';
import '../styles/marketing-fonts.css';
import 'driver.js/dist/driver.css';

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import localFont from 'next/font/local';

import type { User as AuthUser } from 'firebase/auth';
import { appWithTranslation, SSRConfig } from 'next-i18next';

import configuration from '~/configuration';

import FirebaseAppShell from '~/core/firebase/components/FirebaseAppShell';
import FirebaseAuthProvider from '~/core/firebase/components/FirebaseAuthProvider';
import FirebaseAppCheckProvider from '~/core/firebase/components/FirebaseAppCheckProvider';
import ClientAuthSync from '~/core/firebase/components/ClientAuthSync';
import useCollapsible from '~/core/hooks/use-sidebar-state';

import { Organization } from '~/lib/organizations/types/organization';
import { OrganizationContext } from '~/lib/contexts/organization';
import { UserData } from '~/core/session/types/user-data';
import { UserSessionContext } from '~/core/contexts/user-session';
import { UserSession } from '~/core/session/types/user-session';
import { SidebarContext } from '~/core/contexts/sidebar';
import { ThemeContext } from '~/core/contexts/theme';
import {
  CsrfTokenContext,
  type CsrfContextValue,
} from '~/core/contexts/csrf-token';
import { loadSelectedTheme } from '~/core/theming';
import { isBrowser } from '~/core/generic/is-browser';
import { PostHogProvider } from '~/components/PostHogProvider';
import SentryProvider from '~/components/SentryProvider';
import ErrorBoundary from '~/core/ui/ErrorBoundary';
import { retryDynamicImport } from '~/core/ui/retryDynamicImport';
import { ConsentProvider } from '~/core/consent/ConsentProvider';
import ConsentBanner from '~/components/consent/ConsentBanner';

const AppRouteLoadingIndicator = dynamic(
  () => retryDynamicImport(() => import('~/core/ui/AppRouteLoadingIndicator')),
  {
    ssr: false,
    loading: () => null,
  },
);

const SystemAnnouncementsWrapper = dynamic(
  () =>
    retryDynamicImport(
      () => import('~/components/announcements/SystemAnnouncementsWrapper'),
    ),
  {
    ssr: false,
    loading: () => null,
  },
);

const CinematicTourController = dynamic(
  () =>
    retryDynamicImport(
      () => import('~/components/tours/CinematicTourController'),
    ),
  {
    ssr: false,
    loading: () => null,
  },
);

const fontFamilySans = localFont({
  src: '../assets/fonts/inter-latin-wght-normal.woff2',
  variable: '--font-family-sans',
  fallback: ['system-ui', 'Helvetica Neue', 'Helvetica', 'Arial'],
  // Marketing uses Geist; load Inter only when application content uses it.
  preload: false,
  display: 'optional',
  weight: '300 800',
});

// replace the below with your heading
// font of choice or leave as is
const fontFamilyHeading = fontFamilySans;

interface UIState {
  sidebarState: string;
  theme: 'light' | 'dark';
}

interface DefaultPageProps extends SSRConfig {
  session?: Maybe<AuthUser>;
  user?: Maybe<UserData>;
  organization?: Maybe<WithId<Organization>>;
  csrfToken?: string;
  ui?: UIState;
}

function App(
  props: AppProps<DefaultPageProps> & { pageProps: DefaultPageProps },
) {
  const { Component } = props;
  const pageProps = props.pageProps as DefaultPageProps;
  const { emulator, firebase } = configuration;
  const serverUserId = pageProps.session?.uid;
  const router = useRouter();

  const userSessionContext: UserSession = useMemo(() => {
    return {
      auth: pageProps.session,
      data: pageProps.user,
    };
  }, [pageProps]);

  const [organization, setOrganization] = useState<
    DefaultPageProps['organization']
  >(pageProps.organization);

  const [userSession, setUserSession] =
    useState<Maybe<UserSession>>(userSessionContext);

  // CSRF token state - initialized from SSR props, can be refreshed client-side
  const [csrfToken, setCsrfToken] = useState<Maybe<string>>(
    pageProps.csrfToken,
  );

  // Memoize CSRF context value to avoid unnecessary re-renders
  const csrfContextValue = useMemo<CsrfContextValue>(
    () => ({
      token: csrfToken,
      setToken: setCsrfToken,
    }),
    [csrfToken],
  );

  const updateCurrentOrganization = useCallback(() => {
    setOrganization(pageProps.organization);
  }, [pageProps.organization]);

  const updateCurrentUser = useCallback(() => {
    // Update session when:
    // 1. New session exists (navigating to/between protected pages), OR
    // 2. No session AND csrfToken exists (auth page - clear stale state)
    // Skip when: No session AND no csrfToken (public page - preserve UI)
    if (userSessionContext.auth || pageProps.csrfToken) {
      setUserSession(userSessionContext);
    }
  }, [userSessionContext, pageProps.csrfToken]);

  // Update CSRF token when navigating to pages that provide one
  // This fixes the case where user lands on a static page (no CSRF token)
  // then navigates client-side to auth pages that need CSRF protection
  const updateCsrfToken = useCallback(() => {
    if (pageProps.csrfToken) {
      setCsrfToken(pageProps.csrfToken);
    }
  }, [pageProps.csrfToken]);

  useEffect(updateCurrentOrganization, [updateCurrentOrganization]);
  useEffect(updateCurrentUser, [updateCurrentUser]);
  useEffect(updateCsrfToken, [updateCsrfToken]);
  useEffect(() => {
    // Only clear organization on genuine sign-out:
    // userSession is undefined AND there is no SSR-provided organization.
    // This prevents transient auth state flickers (during ClientAuthSync
    // custom token exchange) from clearing the SSR-provided org context.
    if (userSession === undefined && !pageProps.organization) {
      setOrganization(undefined);
    }
  }, [userSession, pageProps.organization]);

  return (
    <ConsentProvider>
      <FirebaseAppShell config={firebase}>
        <FirebaseAppCheckProvider>
          <CsrfTokenContext.Provider value={csrfContextValue}>
            <FirebaseAuthProvider
              userSession={userSession}
              setUserSession={setUserSession}
              useEmulator={emulator}
            >
              <UserSessionContext.Provider
                value={{ userSession, setUserSession }}
              >
                <OrganizationContext.Provider
                  value={{ organization, setOrganization }}
                >
                  <SentryProvider>
                    <PostHogProvider>
                      <AppRouteLoadingIndicator />

                      <UiStateProvider state={pageProps.ui}>
                        <ClientAuthSync serverUserId={serverUserId}>
                          <FontFamily />
                          <SystemAnnouncementsWrapper />
                          <CinematicTourController />
                          <ErrorBoundary
                            fallback={<AppErrorFallback />}
                            resetKey={router.asPath}
                          >
                            <Component {...pageProps} />
                          </ErrorBoundary>
                        </ClientAuthSync>
                      </UiStateProvider>
                    </PostHogProvider>
                  </SentryProvider>
                </OrganizationContext.Provider>
              </UserSessionContext.Provider>
            </FirebaseAuthProvider>
          </CsrfTokenContext.Provider>
        </FirebaseAppCheckProvider>
      </FirebaseAppShell>
      <ConsentBanner />
    </ConsentProvider>
  );
}

export default appWithTranslation<AppProps & { pageProps: DefaultPageProps }>(
  App,
);

function UiStateProvider(
  props: React.PropsWithChildren<{
    state: Maybe<UIState>;
  }>,
) {
  const ui = props.state;
  const isCollapsed = ui?.sidebarState === 'collapsed';
  const currentTheme = ui?.theme ?? configuration.theme;

  const [collapsed, setCollapsed] = useCollapsible(isCollapsed);
  const [theme, setTheme] = useState(currentTheme);

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
      <ThemeContext.Provider value={{ theme, setTheme }}>
        {props.children}
      </ThemeContext.Provider>
    </SidebarContext.Provider>
  );
}

function FontFamily() {
  return (
    <style jsx global>
      {`
        html {
          --font-family-sans:
            '-apple-system', 'BlinkMacSystemFont',
            ${fontFamilySans.style.fontFamily}, 'Segoe UI', 'Roboto', 'Ubuntu',
            'sans-serif';

          --font-family-heading:
            '-apple-system', 'BlinkMacSystemFont',
            ${fontFamilyHeading.style.fontFamily};
        }
      `}
    </style>
  );
}

function AppErrorFallback() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
      <div className="text-center">
        <h1 className="mb-4 text-2xl font-semibold text-gray-900 dark:text-white">
          Something went wrong
        </h1>
        <p className="mb-6 text-gray-600 dark:text-gray-400">
          We encountered an unexpected error. Please try refreshing the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="rounded-md bg-primary-500 px-4 py-2 text-white hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
        >
          Refresh Page
        </button>
      </div>
    </div>
  );
}

if (isBrowser()) {
  loadSelectedTheme();
}
// DEPLOY_MARKER_1764704659
